import { all, allPass, always, complement, compose, equals, filter, findIndex, fromPairs, head, isEmpty, lensIndex, map, of, over, path, prop, propEq, propOr, toLower, toPairs, when } from 'ramda';
import { useEffect, useState } from 'react';
import useSaveSimulationNeedsMutation from './useSaveSimulationNeedsMutation';
import { isNilOrEmpty } from '@@serverHelpers/index.mjs';
import { useForm } from 'react-hook-form';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { offerCodeEnum } from '@@viewEnums';

const useSimulationNeedsForm = ({
  getSimulationNeedsSubscribers,
  getSimulationNeeds,
  getMiddleText,
  getMiddleBoolean,
  middleFeatures,
  tabs,
  updateVisibility,
  isButtonDisabled,
  selectedGrantee,
  viewMode
}) => {
  const [loading, setLoading] = useState(false);
  const subscribers = propOr([], 'subscribers', getSimulationNeedsSubscribers);

  const { processHandlers, context } = useProcess();

  const RECUEIL_BESOIN_CHOIX_LISTE = middleFeatures?.RECUEIL_BESOIN_CHOIX_LISTE ?? [];

  const collectionNeedChoices = compose(
    map(({ LABEL, CODE }) => ({
      label: LABEL,
      value: CODE
    })),
    filter(propEq('IS_DISPLAYED', 'true'))
  )(RECUEIL_BESOIN_CHOIX_LISTE);

  let subscriberNeeds = propOr([], 'subscriberNeeds', getSimulationNeeds);
  const defaultLevel = compose(
    when(isNilOrEmpty, always('')),
    prop('RECUEIL_BESOIN_DEFAULT_LEVEL')
  )(middleFeatures);

  const mainSubscriber = head(subscribers);

  const lowerize = (prop) =>
    map(compose(fromPairs, map(over(lensIndex(0), toLower)), toPairs))(
      getMiddleText(prop, null, '')
    );

  const postes = lowerize('RECUEIL_BESOIN_POSTES');

  const levels = lowerize('RECUEIL_BESOIN_LEVELS');

  const { onFormSubmit } = useSaveSimulationNeedsMutation();

  const defaultNeeds = map(
    (poste) => ({
      name: poste.value,
      level: defaultLevel
    }),
    postes
  );

  const typeSouhait = getMiddleText('RECUEIL_BESOIN_TYPE_SOUHAIT_EST_NECESSAIRE');

  if (equals(viewMode, 'CUSTOMER')) {
    subscriberNeeds = compose(of, head)(subscriberNeeds);
  }

  const $onClickPrev = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      contractOnGoing:
        prop('contractOnGoing', getSimulationNeeds) ||
        getMiddleBoolean('RECUEIL_BESOIN_QUESTION_RESPONSE'),
      subscriberNeeds: subscriberNeeds.map((subscriberNeed) => {
        return {
          id: subscriberNeed.id,
          healthNeeds: defaultNeeds.map((defaultNeed) => {
            const storeValue = subscriberNeed.healthNeeds.find(
              (value) => value.name === defaultNeed.name
            );
            return {
              name: defaultNeed.name,
              level: storeValue?.level || defaultNeed.level
            };
          })
        };
      }),
      collectionNeeds: propOr([], 'collectionNeeds', getSimulationNeeds),
      comment: prop('comment', getSimulationNeeds)
    }
  });

  const {
    formState: { isValid }
  } = form;

  useEffect(() => {
    form.trigger();
  }, []);

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);
    onFormSubmit(values, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };
  let subscriberIndex = findIndex(propEq('id', selectedGrantee.id))(subscriberNeeds);

  const offerCode = path(['offer', 'code'], getSimulationNeeds);

  if (offerCode === offerCodeEnum.GROUP_HEALTH && subscriberIndex === -1) {
    subscriberIndex = 0;
  }

  const selectedSubscriberIsMainSubscriber = tabs.selectedTab === 0;

  const areAllLevelsFilled = all(
    compose(all(allPass([prop('level'), complement(isEmpty)])), prop('healthNeeds')),
    form.watch('subscriberNeeds')
  );

  return {
    onSubmit,
    levels,
    postes,
    subscribers,
    selectedSubscriberIsMainSubscriber,
    $onClickPrev,
    viewMode,
    mainSubscriber,
    offerCode,
    form,
    updateVisibility,
    isButtonDisabled,
    isValid,
    subscriberIndex,
    typeSouhait,
    areAllLevelsFilled,
    collectionNeedChoices
  };
};

export default useSimulationNeedsForm;
